import React from "react";
import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";

function getScoreColour(score) {
  if (score > 85) return "#80f8ce";
  if (score >= 75) return "#ffc46b";
  return "#ffffff";
}

export default function TopPerformersCard({
  title,
  chipLabel,
  subtitle,
  isLoading,
  noDataLabel,
  performers,
  formatNumber,
  locale,
}) {
  return (
    <Box
      sx={{
        px: 0.5,
        color: "#e7f8fe",
        height: "100%",
        minHeight: { xs: 300, sm: 350, md: 400, lg: "auto" },
        background: "linear-gradient(135deg, #0d3d50 0%, #0a3141 100%)",
        borderRadius: 3,
        border: "1px solid rgba(136, 208, 230, 0.36)",
        padding: 1.5,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.45 }}
      >
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            backgroundColor: "rgba(11, 50, 66, 0.45)",
            color: "#d8f1f9",
            border: "1px solid rgba(156, 220, 239, 0.45)",
          }}
        />
      </Stack>
      <Typography
        variant="caption"
        sx={{
          color: "#abdae9",
          display: "block",
          mb: 0.6,
          pb: 0.7,
          borderBottom: "1px solid rgba(156, 220, 239, 0.35)",
        }}
      >
        {subtitle}
      </Typography>
      <Stack spacing={0}>
        {!isLoading && performers.length === 0 && (
          <Typography variant="caption" sx={{ color: "#abdae9" }}>
            {noDataLabel}
          </Typography>
        )}
        {performers.map((performer, index) => (
          <Box
            key={performer.name}
            sx={{
              py: 0.55,
              borderBottom:
                index < performers.length - 1
                  ? "1px solid rgba(156, 220, 239, 0.22)"
                  : "none",
            }}
          >
            <Stack direction="row" alignItems="stretch" spacing={1}>
              <Box
                sx={{
                  width: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 18px",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: "#89def6",
                    fontSize: "0.86rem",
                    lineHeight: 1,
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack spacing={0.28}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {performer.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: getScoreColour(performer.score),
                          fontSize: "0.9rem",
                        }}
                      >
                        {performer.score}%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#a8d9e8",
                          fontWeight: 700,
                          fontSize: "0.74rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatNumber(performer.area, locale)} m²
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box sx={{ width: "100%", mt: 0.35 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, performer.score))}
                      sx={{
                        width: "100%",
                        height: 4,
                        borderRadius: 999,
                        backgroundColor: "rgba(160, 212, 230, 0.24)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          background:
                            performer.score > 85
                              ? "linear-gradient(90deg, #18b977 0%, #80f8ce 100%)"
                              : performer.score >= 75
                                ? "linear-gradient(90deg, #c88418 0%, #ffc46b 100%)"
                                : "linear-gradient(90deg, #d9e7ee 0%, #ffffff 100%)",
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
